import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { isOwnerAuth } from '../utils/auth'
import { apiFetch } from '../utils/api'

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint)
  useEffect(() => {
    let t
    const handler = () => { clearTimeout(t); t = setTimeout(() => setIsMobile(window.innerWidth < breakpoint), 80) }
    window.addEventListener('resize', handler)
    return () => { clearTimeout(t); window.removeEventListener('resize', handler) }
  }, [breakpoint])
  return isMobile
}
import { getProfile, saveProfile, embedChunks, getChunksSample, getRepresentativeChunks, getChunksStats, clearChunks, extractFacts, extractFactsStart, getFacts, recalculate, getFactContradictions, getChunkSettings, getPublicParticipant, updateParticipantVisibility, getSurveyStatus, getSurveyGroup1Questions, generateSurveyGroup2, generateSurveySession, submitSurvey, analyzeSurvey, getSurveyResponses, clearSurvey, getFactsExtractionStatus } from '../utils/api'
import { getModelPref } from '../utils/modelPrefs'
import ModelToggle from './ModelToggle'
import { VisibilitySettings } from './PublicParticipantPage'
import { analyzeAll } from '../utils/analyze'
import { filterMyMessages, buildContextualChunks } from '../utils/filters'
import FileUpload from './FileUpload'
import Mindmap from './Mindmap'
import CareerNavigator from './CareerNavigator'
import NatalChart from './NatalChart'
import VisitorChat from './VisitorChat'
import Development from './Development'
import Mentor from './Mentor'
import ProfileResult, { C, font, GlassPanel } from './ProfileResult'
import SocionicsTab from './SocionicsTab'
import PhysiognomyTab from './PhysiognomyTab'
import MusicTab from './MusicTab'
import Survey, { SurveyAnalyzingScreen, SurveyDoneScreen } from './Survey'
import SurveyChoice from './SurveyChoice'
import { SurveyEvidencePanel } from './EvidencePanel'

function SurveyResultTab({ userId, surveyResponses }) {
  const [responses, setResponses] = useState(surveyResponses)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!responses.length) {
      setLoading(true)
      getSurveyResponses(userId).then(d => setResponses(d.responses || [])).finally(() => setLoading(false))
    }
  }, [userId])
  if (loading) return <div style={{ padding: '40px 0', textAlign: 'center', color: '#8890aa', fontSize: 13 }}>Загрузка...</div>
  return <SurveyEvidencePanel responses={responses} />
}
import FactsViewer from './FactsViewer'
import VoiceNotesViewer from './VoiceNotesViewer'
import { chunkResumeText } from '../utils/parsers'

const BASE_URL = import.meta.env.VITE_API_URL || 'http://92.53.107.15'

const STEPS = [
  { key: 'parsing',   label: 'Парсинг данных...' },
  { key: 'chunking',  label: 'Построение контекста...' },
  { key: 'style',     label: 'Анализ стилистики...' },
  { key: 'analysis',  label: 'AI-анализ личности...' },
  { key: 'saving',    label: 'Сохранение профиля...' },
]

function buildContextFromDBChunks(chunks) {
  const byType = { journal: [], resume: [], threads: [], personal: [], group: [] }
  for (const c of chunks) {
    const t = c.chatType || 'group'
    if (byType[t]) byType[t].push(c.text)
    else byType.group.push(c.text)
  }
  const parts = []
  if (byType.journal.length)  parts.push('## Личный дневник (первичный источник)\n' + byType.journal.join('\n\n---\n\n'))
  if (byType.resume.length)   parts.push('## Резюме\n' + byType.resume.join('\n\n---\n\n'))
  if (byType.threads.length)  parts.push('## Threads-посты\n' + byType.threads.join('\n\n---\n\n'))
  if (byType.personal.length) parts.push('## Личные переписки 1:1\n' + byType.personal.join('\n\n---\n\n'))
  if (byType.group.length)    parts.push('## Групповые чаты (мат = манера общения, не черта характера)\n' + byType.group.join('\n\n---\n\n'))
  return parts.join('\n\n====\n\n')
}

function buildContext({ telegram, journal, resume, threads }) {
  const parts = []
  if (telegram?.chunks?.length) {
    const all = telegram.chunks
    const personal = all.filter(c => c.chatType === 'personal')
    const group = all.filter(c => c.chatType !== 'personal')
    const pick = (arr, n) => arr.length <= n ? arr : Array.from({ length: n }, (_, i) => arr[Math.floor(i * arr.length / n)])
    if (personal.length) parts.push(`## Личные переписки 1:1\n${pick(personal, 2).map(c => c.text).join('\n\n---\n\n')}`)
    if (group.length) parts.push(`## Групповые чаты (мат = манера общения)\n${pick(group, 2).map(c => c.text).join('\n\n---\n\n')}`)
  }
  if (journal?.length) {
    const sample = journal.slice(0, 3).map(e => `[${e.date}] ${e.text.slice(0, 400)}`).join('\n\n')
    parts.push(`## Личный дневник (первичный источник)\n${sample}`)
  }
  if (resume) parts.push(`## Резюме\n${resume.slice(0, 1500)}`)
  if (threads) {
    const posts = (threads.posts || []).slice(0, 12).map(p => p.text).filter(Boolean).join('\n')
    if (posts) parts.push(`## Threads посты\n${posts}`)
    const replies = (threads.replies || []).slice(0, 8).map(p => p.text).filter(Boolean).join('\n')
    if (replies) parts.push(`## Threads комментарии\n${replies}`)
    if (threads.threadsMyName && threads.dm?.length) {
      const myDm = threads.dm.filter(m => m.from === threads.threadsMyName)
      const dmText = myDm.slice(0, 12).map(m => m.text).filter(Boolean).join('\n')
      if (dmText) parts.push(`## Личные сообщения Threads\n${dmText}`)
    }
  }
  return parts.join('\n\n====\n\n')
}

function ProgressBar({ step }) {
  const idx = STEPS.findIndex(s => s.key === step)
  const pct = idx < 0 ? 0 : Math.round(((idx + 1) / STEPS.length) * 100)
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontFamily: font.body, fontSize: 13, color: C.textMuted }}>
        <span>{STEPS[idx]?.label}</span>
        <span>{pct}%</span>
      </div>
      <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: C.accent, borderRadius: 2, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  )
}

const ANALYSIS_TABS = [
  { id: 'personality',  label: 'Личность' },
  { id: 'competencies', label: 'Компетенции' },
  { id: 'career',       label: 'Карьера' },
  { id: 'motivation',   label: 'Мотивация' },
  { id: 'socionics',    label: 'Соционика' },
  { id: 'physiognomy',  label: 'Физиогномика', guestHidden: true },
  { id: 'survey',       label: 'Анкета' },
]

export default function ParticipantView({ participantSession, onLogout, isGuest = false, isOwnerImpersonating = false, onStopImpersonating }) {
  const userId = `participant_${participantSession.participant_id}`
  const [displayName, setDisplayName] = useState(participantSession.display_name || '')
  const isMobile = useIsMobile()

  const [profile, setProfile]               = useState(null)
  const [profileLoading, setProfileLoading] = useState(true)
  const [loading, setLoading]               = useState(false)
  const [step, setStep]                     = useState(null)
  const [error, setError]                   = useState(null)
  const [msg, setMsg]                       = useState(null)
  const [view, setView]                     = useState('profile')
  const [analysisTab, setAnalysisTab]       = useState('personality')
  const [showUpload, setShowUpload]         = useState(false)
  const [twinLoading, setTwinLoading]       = useState(false)
  const [twinConfigured, setTwinConfigured] = useState(false)
  const [clearConfirm, setClearConfirm]     = useState(false)
  const [factsLoading, setFactsLoading]     = useState(false)
  const [factsCount, setFactsCount]         = useState(null)
  const [factsSubTab, setFactsSubTab]       = useState('facts')
  const [extractionRunning, setExtractionRunning] = useState(false)
  const [extractionJobId, setExtractionJobId] = useState(null)
  const [recalcLoading, setRecalcLoading]   = useState(false)
  const [recalcStages, setRecalcStages]     = useState(null)
  const [showFacts, setShowFacts]           = useState(false)
  const [careerRolesState, setCareerRolesState] = useState(null)
  const [dataStats, setDataStats]               = useState(null)
  const [pendingIngestReady, setPendingIngestReady] = useState(false) // runIngest done this session, ready to analyze
  const [publicSettings, setPublicSettings]     = useState(null)
  const [visibilitySaving, setVisibilitySaving] = useState(false)
  const [linkCopied, setLinkCopied]             = useState(false)
  const [participantIsOwner, setParticipantIsOwner] = useState(false)
  // Survey
  const [surveyStatus, setSurveyStatus]     = useState(null)  // {group1_done, group2_done}
  const [surveyStep, setSurveyStep]         = useState(null)  // 'choice'|'group1'|'group2'|'analyzing'
  const [surveyQuestions, setSurveyQuestions] = useState([])
  const [surveyGroupNum, setSurveyGroupNum] = useState(1)
  const [surveyLoading, setSurveyLoading]   = useState(false)
  const [surveyResponses, setSurveyResponses] = useState([])
  const [surveySessionsCompleted, setSurveySessionsCompleted] = useState(0)
  const [surveySessionLoading, setSurveySessionLoading] = useState(false)
  const [showSurveyAnalyzeBtn, setShowSurveyAnalyzeBtn] = useState(false)
  const scrollRef = useRef(null)

  const participantId = participantSession.participant_id
  const publicUrl = `${window.location.origin}/u/${participantId}`

  // Viewer can edit visibility only if: logged in (not guest) AND not an owner visiting another owner
  const isOwnerVisiting = isOwnerAuth() || localStorage.getItem('dt_owner') === 'true'
  const canEditVisibility = !isGuest && !(isOwnerVisiting && participantIsOwner)

  useEffect(() => {
    Promise.all([
      getProfile(userId),
      getSurveyStatus(userId),
    ]).then(([data, status]) => {
      setSurveyStatus(status)
      setSurveySessionsCompleted(status.group2_sessions_completed || 0)
      if (status.group1_done || status.group2_done) {
        getSurveyResponses(userId).then(d => setSurveyResponses(d.responses || [])).catch(() => {})
      }
      if (data && !data.error) {
        setProfile({
          personality_data:  data.personality_data,
          competencies_data: data.competencies_data,
          career_data:       data.career_data,
          motivation_data:   data.motivation_data,
          style_analysis:    data.metadata?.style_analysis,
        })
        if (data.metadata?.bot_prompt) setTwinConfigured(true)
        // Если есть профиль — не показываем выбор
      } else {
        // Нет профиля
        if (status.group2_done) {
          // Анкета пройдена, но профиль не создан — запустить анализ
          setSurveyStep('analyzing')
          runSurveyAnalysis()
        } else if (status.group1_done) {
          // Group1 пройдена, group2 нет — автоматически возобновляем group2 (не показываем choice)
          resumeGroup2(userId)
        } else {
          setSurveyStep('choice')
        }
      }
    })
      .catch(() => { setShowUpload(true) })
      .finally(() => setProfileLoading(false))
    getFacts(userId).then(r => { if (r.total > 0) setFactsCount(r.total) }).catch(() => {})
    getFactsExtractionStatus(userId).then(s => { if (s.running) setExtractionRunning(true) }).catch(() => {})
    getChunksStats(userId).then(s => { if (!s.error) setDataStats(s) }).catch(() => {})
    getPublicParticipant(participantId).then(d => {
      setPublicSettings(d.public_settings)
      if (d.is_owner) setParticipantIsOwner(true)
      if (d.display_name && !participantSession.display_name) setDisplayName(d.display_name)
      // В гостевом режиме грузим профиль из публичного эндпоинта напрямую
      if (isGuest && d.profile) {
        setProfile({
          personality_data:  d.profile.personality_data,
          competencies_data: d.profile.competencies_data,
          career_data:       d.profile.career_data,
          motivation_data:   d.profile.motivation_data,
          style_analysis:    d.profile.metadata?.style_analysis,
          extracted_knowledge: d.profile.extracted_knowledge,
        })
        setProfileLoading(false)
      } else if (isGuest) {
        setProfileLoading(false)
      }
    }).catch(() => { if (isGuest) setProfileLoading(false) })
  }, [userId])

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [view])

  // ── Survey helpers ──────────────────────────────────────────────────────────

  async function resumeGroup2(uid) {
    setSurveyLoading(true)
    setError(null)
    try {
      const result = await generateSurveyGroup2(uid)
      if (result.error) throw new Error(result.error)
      setSurveyQuestions(result.questions || [])
      setSurveyGroupNum(2)
      setSurveyStep('group2')
    } catch (e) {
      setError('Ошибка загрузки вопросов анкеты: ' + e.message)
      setSurveyStep('choice')
    } finally {
      setSurveyLoading(false)
    }
  }

  async function startSurvey() {
    // Если group1 уже пройдена — возобновляем с group2
    if (surveyStatus?.group1_done && !surveyStatus?.group2_done) {
      return resumeGroup2(userId)
    }
    setSurveyLoading(true)
    try {
      const data = await getSurveyGroup1Questions()
      setSurveyQuestions(data.questions || [])
      setSurveyGroupNum(1)
      setSurveyStep('group1')
    } catch (e) {
      setError('Ошибка загрузки вопросов: ' + e.message)
    } finally {
      setSurveyLoading(false)
    }
  }

  async function handleSurveySubmit(responses) {
    setSurveyLoading(true)
    const isExtraSession = surveyGroupNum >= 200
    const sessionNum = isExtraSession ? surveyGroupNum - 200 : 1
    const groupNum = isExtraSession ? 2 : surveyGroupNum
    try {
      await submitSurvey(userId, groupNum, responses, sessionNum)
      if (surveyGroupNum === 1) {
        // Генерируем вторую группу
        const result = await generateSurveyGroup2(userId)
        if (result.error) throw new Error(result.error)
        setSurveyQuestions(result.questions || [])
        setSurveyGroupNum(2)
        setSurveyStep('group2')
      } else if (isExtraSession) {
        // Extra session завершена — обновить счётчик, показать кнопку анализа
        setSurveySessionsCompleted(sessionNum)
        setSurveyStep(null)
        setShowSurveyAnalyzeBtn(true)
        getSurveyResponses(userId).then(d => setSurveyResponses(d.responses || [])).catch(() => {})
      } else {
        // Обычная group2 завершена — запускаем анализ
        setSurveyStep('analyzing')
        await runSurveyAnalysis()
      }
    } catch (e) {
      setError('Ошибка анкеты: ' + e.message)
      setSurveyStep(surveyGroupNum === 1 ? 'group1' : isExtraSession ? null : 'group2')
    } finally {
      setSurveyLoading(false)
    }
  }

  async function startExtraSession() {
    if (surveySessionsCompleted >= 5) return
    setSurveySessionLoading(true)
    setError(null)
    try {
      const nextSession = surveySessionsCompleted + 1
      const result = await generateSurveySession(userId, nextSession)
      if (result.error) throw new Error(result.error)
      setSurveyQuestions(result.questions || [])
      setSurveyGroupNum(200 + nextSession) // encode: 200+session for submit handler
      setSurveyStep('group2_extra')
    } catch (e) {
      setError('Ошибка генерации вопросов: ' + e.message)
    } finally {
      setSurveySessionLoading(false)
    }
  }

  async function runSurveyAnalysis() {
    setError(null)
    try {
      const result = await analyzeSurvey(userId)
      if (result?.error) throw new Error(result.error)
      if (result?.personality) {
        await saveProfile(userId, {
          personality: result.personality,
          competencies: result.competencies,
          career: result.career,
          motivation: result.motivation,
          evidence_data: result.evidence,
        })
        setProfile({
          personality_data:  result.personality,
          competencies_data: result.competencies,
          career_data:       result.career,
          motivation_data:   result.motivation,
        })
        setSurveyStatus({ group1_done: true, group2_done: true })
        setSurveyStep('done')
      }
    } catch (e) {
      setError('Ошибка анализа: ' + e.message)
      setSurveyStep('choice')
    }
  }

  // Загрузка данных: парсит, чанкует, встраивает в БД. Анализ НЕ запускает —
  // это отдельный явный шаг (runAnalyzeNow).
  async function runIngest(sources) {
    setLoading(true); setError(null); setMsg(null); setShowUpload(false)
    try {
      const tick = () => new Promise(r => setTimeout(r, 50))

      // Шаг 1: парсинг telegram
      setStep('parsing')
      await tick()
      let telegramData = null
      if (sources.telegram) {
        const myMsgs = filterMyMessages(sources.telegram.messages, sources.telegram.myName)
        const chunks = buildContextualChunks(myMsgs)
        telegramData = { chunks }
      }

      // Шаг 2: embed всех источников в DB
      setStep('chunking')
      await tick()
      const allChunks = []
      if (telegramData?.chunks?.length) {
        telegramData.chunks.forEach(c => allChunks.push({ text: c.text, source: `tg_${c.chatName || 'chat'}`, chatType: c.chatType || 'group', dateRange: c.dateRange || '' }))
      }
      if (sources.journal?.length) {
        sources.journal.forEach((j, i) => { if (j.text) allChunks.push({ text: j.text.slice(0, 2000), source: `journal_${i}`, chatType: 'journal', dateRange: j.date || '' }) })
      }
      if (sources.resume) {
        chunkResumeText(sources.resume).forEach(t => allChunks.push({ text: t, source: 'resume', chatType: 'resume', dateRange: '' }))
      }
      if (sources.threads?.posts?.length) {
        sources.threads.posts.slice(0, 30).forEach((t, i) => { if (t.text) allChunks.push({ text: t.text.slice(0, 500), source: `threads_post_${i}`, chatType: 'threads', dateRange: t.date || '' }) })
      }
      if (sources.threads?.replies?.length) {
        sources.threads.replies.slice(0, 20).forEach((t, i) => { if (t.text) allChunks.push({ text: t.text.slice(0, 500), source: `threads_reply_${i}`, chatType: 'threads', dateRange: t.date || '' }) })
      }
      if (sources.threads?.threadsMyName && sources.threads?.dm?.length) {
        const myDMs = sources.threads.dm.filter(m => m.from === sources.threads.threadsMyName)
        myDMs.slice(0, 50).forEach((m, i) => { if (m.text) allChunks.push({ text: m.text.slice(0, 500), source: `threads_dm_${i}`, chatType: 'personal', dateRange: m.date || '' }) })
      }
      if (allChunks.length > 0) {
        try { await embedChunks(userId, allChunks) } catch (e) { console.warn('[ParticipantView] embedChunks failed:', e) }
      }

      setPendingIngestReady(true)
      getChunksStats(userId).then(s => { if (!s.error) setDataStats(s) }).catch(() => {})
      setMsg('Данные загружены. Можно проверить факты или запустить анализ, когда будешь готов.')
    } catch (e) {
      setError(`Ошибка загрузки: ${e.message}`)
      if (!profile) setShowUpload(true)
    } finally {
      setLoading(false); setStep(null)
    }
  }

  // Запускает сам анализ поверх уже загруженных в БД данных. Требует, чтобы runIngest
  // отработал в этой же сессии — если сессия потеряна (перезагрузка страницы), просит
  // загрузить файл заново.
  async function runAnalyzeNow() {
    if (!pendingIngestReady) {
      setError('Сессия загрузки истекла — загрузи файл ещё раз, чтобы запустить анализ.')
      setShowUpload(true)
      return
    }
    setLoading(true); setError(null); setMsg(null)
    try {
      const tick = () => new Promise(r => setTimeout(r, 50))

      // DB-first контекст — берём репрезентативную выборку ВСЕХ накопленных чанков
      setStep('style')
      await tick()
      let mainContext = ''
      try {
        const settingsRes = await getChunkSettings(userId)
        const userSettings = settingsRes?.settings || {}
        const repN = userSettings.n || 40
        const repQuotas = userSettings.quotas || null
        const repResult = await getRepresentativeChunks(userId, repN, repQuotas)
        if (repResult?.chunks?.length) {
          mainContext = buildContextFromDBChunks(repResult.chunks)
        }
      } catch (e) { console.warn('[ParticipantView] representative chunks failed:', e) }

      if (!mainContext.trim()) throw new Error('Нет данных для анализа в базе.')

      setStep('analysis')
      const settingsForAnalyze = await getChunkSettings(userId).catch(() => ({}))
      const result = await analyzeAll(mainContext, { user_id: userId, fast_mode: settingsForAnalyze?.settings?.fast_mode || false })
      const { personality, competencies, career, motivation, evidence, usage } = result

      setStep('saving')
      const saveResult = await saveProfile(userId, {
        personality, competencies, career, motivation,
        evidence_data: evidence || null,
        tokens_used: (usage?.input_tokens || 0) + (usage?.output_tokens || 0),
        cost_usd: usage?.cost_usd || 0,
      })
      setProfile({ personality_data: personality, competencies_data: competencies, career_data: career, motivation_data: motivation, evidence_data: evidence || null, _evidence: evidence || {} })
      getChunksStats(userId).then(s => { if (!s.error) setDataStats(s) }).catch(() => {})
      setPendingIngestReady(false)
      setShowUpload(false)
      const costStr = usage?.cost_usd ? ` · $${usage.cost_usd.toFixed(4)}` : ''
      setMsg(`Профиль обновлён (запуск №${saveResult?.run_count || 1})${costStr}`)
    } catch (e) {
      setError(`Ошибка анализа: ${e.message}`)
    } finally {
      setLoading(false); setStep(null)
    }
  }

  async function runRecalculate() {
    try {
      const { contradictions } = await getFactContradictions(userId)
      const pending = contradictions?.length || 0
      if (pending > 0 && !confirm(`${pending} неразрешённых противоречий в фактах. Пересчитать всё равно?`)) return
    } catch { /* soft warning only */ }

    setRecalcLoading(true)
    setRecalcStages(null)
    setError(null)
    try {
      const res = await recalculate(userId, getModelPref('personality'), getModelPref('socionics'))
      const { job_id } = res
      let attempts = 0
      while (attempts < 165) {
        await new Promise(r => setTimeout(r, 4000))
        attempts++
        const jr = await fetch(`${BASE_URL}/api/analyze/job/${job_id}`)
        if (!jr.ok) { await new Promise(r => setTimeout(r, 4000)); continue }
        const job = await jr.json()
        if (job.stages) setRecalcStages(job.stages)
        if (job.status === 'done') {
          const result = job.result
          setProfile({
            personality_data: result.personality,
            competencies_data: result.competencies,
            career_data: result.career,
            motivation_data: result.motivation,
            _evidence: result.evidence || {},
            evidence_data: result.evidence || null,
          })
          const costStr = result.usage?.cost_usd ? ` · $${result.usage.cost_usd.toFixed(4)}` : ''
          setMsg(`Личность и соционика пересчитаны${costStr}`)
          return
        }
        if (job.status === 'error') throw new Error(job.error || 'Ошибка пересчёта')
      }
      throw new Error('Таймаут пересчёта')
    } catch (e) {
      setError(`Ошибка пересчёта: ${e.message}`)
    } finally {
      setRecalcLoading(false)
      setRecalcStages(null)
    }
  }

  async function configureTwin() {
    if (!profile) return
    setTwinLoading(true)
    setMsg(null)
    try {
      const res = await apiFetch('/api/bot/generate-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      setTwinConfigured(true)
      setMsg('Стиль двойника настроен')
    } catch (e) {
      setMsg(`Ошибка: ${e.message}`)
    } finally {
      setTwinLoading(false)
    }
  }

  const ps = publicSettings || {}
  const navItems = [
    { id: 'profile',   label: isGuest ? 'Профиль' : 'Мой профиль',                         hidden: isGuest && ps.show_personality === false },
    { id: 'mindmap',   label: 'Mindmap',                                                     hidden: isGuest && ps.show_mindmap === false },
    { id: 'career',    label: 'Карьера',                                                     hidden: isGuest && ps.show_career === false },
    { id: 'natal',     label: 'Натальная карта',                                             hidden: isGuest && ps.show_natal === false },
    { id: 'development', label: 'Развитие' },
    { id: 'mentor',      label: 'Наставник' },
    { id: 'facts',     label: factsCount !== null ? `Факты · ${factsCount}` : 'Факты',       hidden: isGuest },
    { id: 'music',     label: 'Музыка',                                                       hidden: isGuest && ps.show_music === false },
    { id: 'twin',      label: isGuest ? 'Чат с двойником' : 'Мой двойник',                  hidden: isGuest && ps.show_chat === false },
  ].filter(n => !n.hidden)

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg, color: C.text, fontFamily: font.body, overflow: 'hidden', paddingTop: isOwnerImpersonating ? 34 : 0 }}>

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes pulseGold{0%,100%{box-shadow:0 0 0 0 rgba(224,184,74,0.5)}50%{box-shadow:0 0 14px 4px rgba(224,184,74,0.55)}}
        .pulse-gold{animation:pulseGold 1.8s ease-in-out infinite;border-color:rgba(224,184,74,0.65)!important;color:#e0b84a!important}`}</style>
      {/* Статичный звёздный фон */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, backgroundImage: 'url(/stars.svg)', backgroundSize: 'cover', backgroundPosition: 'center', pointerEvents: 'none' }} />

      {/* Баннер режима просмотра от имени участника */}
      {isOwnerImpersonating && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
          background: 'rgba(200,169,110,0.13)', borderBottom: '1px solid rgba(200,169,110,0.28)',
          backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 16px', fontFamily: font.body, fontSize: 12,
        }}>
          <span style={{ color: '#c8a96e' }}>
            👁 Просматривается как: <strong>{displayName}</strong>
          </span>
          <button
            onClick={onStopImpersonating}
            style={{
              background: 'rgba(200,169,110,0.1)', border: '1px solid rgba(200,169,110,0.35)',
              color: '#c8a96e', padding: '3px 12px', borderRadius: 5, cursor: 'pointer',
              fontFamily: font.body, fontSize: 11,
            }}
          >
            ← Вернуться к своему профилю
          </button>
        </div>
      )}

      {/* Nav */}
      <nav style={{
        position: 'relative', zIndex: 100,
        borderBottom: `1px solid ${C.border}`,
        background: 'rgba(5,5,13,0.82)',
        backdropFilter: 'blur(24px)',
        flexShrink: 0,
      }}>
        {/* Строка 1: имя + главные вкладки */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', height: 44, minWidth: 0 }}>
          <div style={{ fontFamily: font.heading, fontSize: 14, fontWeight: 300, letterSpacing: '0.08em', color: C.silverHi, display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
            <div style={{ width: 14, height: 14, borderRadius: '50%', border: `1px solid ${C.borderHi}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, color: C.silver }}>∴</div>
            {displayName}
          </div>
          <div style={{ flex: 1, display: 'flex', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none', touchAction: 'pan-x' }}>
            {navItems.map(n => (
              <button key={n.id} onClick={() => setView(n.id)} style={{
                padding: '0 12px', height: 44, cursor: 'pointer', flexShrink: 0,
                fontFamily: font.body, fontSize: 11.5, fontWeight: 400,
                background: 'none', border: 'none',
                borderBottom: `2px solid ${view === n.id ? C.silverHi : 'transparent'}`,
                color: view === n.id ? C.silverHi : C.silverLo,
                transition: 'all 0.2s', whiteSpace: 'nowrap',
              }}>
                {n.label}
              </button>
            ))}
          </div>
        </div>

        {/* Строка 2: действия (скрыта для гостей) */}
        {!isGuest && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 12px 6px', borderTop: `1px solid ${C.border}`,
          }}>
            {isOwnerImpersonating ? (
              <button onClick={onStopImpersonating} style={{
                padding: '3px 10px', borderRadius: 4,
                border: '1px solid rgba(200,169,110,0.35)', background: 'none',
                color: '#c8a96e', fontFamily: font.body, fontSize: 11, cursor: 'pointer',
              }}>← Вернуться</button>
            ) : (
              <button onClick={onLogout} style={{
                padding: '3px 10px', borderRadius: 4,
                border: `1px solid ${C.border}`, background: 'none',
                color: C.silverLo, fontFamily: font.body, fontSize: 11, cursor: 'pointer',
              }}>↩ Выйти</button>
            )}

            <button
              onClick={() => !recalcLoading && runRecalculate()}
              disabled={recalcLoading || !profile}
              title="Пересчитать личность и соционику на основе проверенных фактов и голоса"
              style={{ padding: '3px 10px', borderRadius: 4, border: `1px solid rgba(224,152,88,0.4)`, background: 'none', color: recalcLoading ? C.orange : C.silverLo, fontFamily: font.body, fontSize: 11, cursor: recalcLoading ? 'wait' : 'pointer', opacity: recalcLoading || !profile ? 0.6 : 1 }}
            >
              {recalcLoading
                ? (recalcStages
                    ? recalcStages.map(s => `${s.label}${s.status === 'done' ? ' ✓' : s.status === 'running' ? '…' : s.status === 'retrying' ? ' ↺' : s.status === 'error' ? ' ✗' : ''}`).join(' → ')
                    : '⏳ Пересчёт...')
                : '⟳ Пересчитать'}
            </button>
            {!recalcLoading && <ModelToggle prefKey="personality" />}

            {clearConfirm ? (
              <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'rgba(220,90,90,0.8)', fontFamily: font.body }}>Удалить данные?</span>
                <button onClick={async () => {
                  setClearConfirm(false)
                  const r = await clearChunks(userId)
                  if (r.error) { setError(`Ошибка: ${r.error}`); return }
                  setProfile(null); setShowUpload(true); setTwinConfigured(false)
                  setMsg(`Профиль удалён (${r.deleted} чанков)`)
                }} style={{ padding: '2px 8px', background: 'rgba(220,90,90,0.15)', color: 'rgba(220,90,90,0.9)', border: '1px solid rgba(220,90,90,0.5)', borderRadius: 4, fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}>да</button>
                <button onClick={() => setClearConfirm(false)} style={{ padding: '2px 8px', background: 'none', color: C.silverLo, border: `1px solid ${C.border}`, borderRadius: 4, fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}>нет</button>
              </span>
            ) : (
              <button onClick={() => setClearConfirm(true)} style={{
                padding: '3px 10px', borderRadius: 4,
                border: `1px solid ${C.border}`, background: 'none',
                color: 'rgba(220,90,90,0.5)', fontFamily: font.body, fontSize: 11, cursor: 'pointer',
              }}>✕ Очистить данные</button>
            )}
          </div>
        )}
        {/* Для гостей — ссылка на главную */}
        {isGuest && (
          <div style={{ padding: '4px 12px 6px', borderTop: `1px solid ${C.border}` }}>
            <a href="/" style={{ fontFamily: font.body, fontSize: 11, color: C.textMuted, textDecoration: 'none', opacity: 0.7 }}>
              ← ai-digital-twin.ru
            </a>
          </div>
        )}
      </nav>

      {/* Content — скроллируемая область */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: view === 'mindmap' ? 'hidden' : 'auto', overflowX: 'hidden', position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Mindmap — full height, no padding */}
        {view === 'mindmap' && (
          <div style={{ flex: 1, minHeight: 0 }}>
            <Mindmap profile={profile} />
          </div>
        )}

        {view !== 'mindmap' && (
        <div style={{ padding: '24px 28px 60px' }}>

          {error && (
            <div style={{ marginBottom: 16, padding: '10px 16px', background: 'rgba(212,90,90,0.1)', border: '1px solid rgba(212,90,90,0.3)', borderRadius: 8, color: C.red, fontSize: 13 }}>
              {error}
            </div>
          )}
          {msg && (
            <div style={{ marginBottom: 16, padding: '10px 16px', background: 'rgba(77,200,138,0.1)', border: '1px solid rgba(77,200,138,0.3)', borderRadius: 8, color: C.green, fontSize: 13 }}>
              {msg}
            </div>
          )}

          {/* Профиль */}
          {view === 'profile' && (
            <>
              {loading && step && <ProgressBar step={step} />}

              {profileLoading && (
                <div style={{ textAlign: 'center', padding: '80px 0', color: C.silverLo }}>
                  Загрузка профиля...
                </div>
              )}

              {!profileLoading && !profile && !loading && !isGuest && surveyStep === 'choice' && (
                <SurveyChoice
                  hasSurvey={surveyStatus?.group1_done || surveyStatus?.group2_done}
                  hasData={false}
                  onChooseSurvey={() => { setSurveyStep(null); startSurvey() }}
                  onChooseUpload={() => { setSurveyStep(null); setShowUpload(true) }}
                />
              )}
              {!profileLoading && !profile && !loading && !isGuest && !surveyStep && showUpload && (
                <FileUpload onReady={runIngest} />
              )}
              {/* Данные загружены, анализ ещё не запускался — пустой профиль + явный триггер */}
              {!profileLoading && !profile && !loading && !isGuest && !surveyStep && !showUpload && dataStats && dataStats.total > 0 && (
                <GlassPanel style={{ padding: '32px 28px', textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontFamily: font.heading, fontSize: 22, color: C.silverHi, marginBottom: 8 }}>
                    Данные загружены
                  </div>
                  <div style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, marginBottom: 22 }}>
                    В базе {dataStats.total} чанков. Анализ ещё не запускался.
                  </div>
                  {pendingIngestReady ? (
                    <button
                      onClick={runAnalyzeNow}
                      disabled={loading}
                      style={{ padding: '10px 28px', borderRadius: 8, border: `1px solid ${C.accent}`, background: C.accentDim, color: C.silverHi, fontFamily: font.body, fontSize: 14, cursor: 'pointer' }}
                    >
                      ▶ Начать анализ
                    </button>
                  ) : (
                    <>
                      <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginBottom: 14 }}>
                        Чтобы запустить анализ сейчас, загрузи файл ещё раз.
                      </div>
                      <button
                        onClick={() => setShowUpload(true)}
                        style={{ padding: '8px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', color: C.silverLo, fontFamily: font.body, fontSize: 13, cursor: 'pointer' }}
                      >
                        ↑ Загрузить данные
                      </button>
                    </>
                  )}
                </GlassPanel>
              )}
              {!profileLoading && !loading && !isGuest && (surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'group2_extra') && (
                <Survey
                  questions={surveyQuestions}
                  groupNum={surveyGroupNum >= 200 ? 2 : surveyGroupNum}
                  totalGroups={2}
                  onSubmit={handleSurveySubmit}
                  loading={surveyLoading}
                />
              )}
              {!isGuest && surveyStep === 'analyzing' && (
                <SurveyAnalyzingScreen />
              )}
              {!isGuest && surveyStep === 'done' && profile && (
                <SurveyDoneScreen
                  personality={profile.personality_data}
                  onViewProfile={() => setSurveyStep(null)}
                />
              )}

              {!profileLoading && !profile && !loading && isGuest && (
                <div style={{ textAlign: 'center', padding: '80px 0', color: C.textMuted, fontFamily: font.body, fontSize: 13 }}>
                  Профиль ещё не создан
                </div>
              )}

              {!profileLoading && profile && !loading && !(surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'group2_extra' || surveyStep === 'analyzing' || surveyStep === 'done') && (
                <>
                  {!isGuest && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
                      {!surveyStatus?.group2_done && (
                        <button onClick={startSurvey} style={{
                          padding: '5px 14px', borderRadius: 20,
                          border: `1px solid ${C.border}`, background: 'none',
                          color: C.textMuted, fontFamily: font.body, fontSize: 11, cursor: 'pointer',
                        }}>
                          ◈ Пройти анкету
                        </button>
                      )}
                      {surveyStatus?.group2_done && (
                        <>
                          <button onClick={startSurvey} style={{
                            padding: '5px 14px', borderRadius: 20,
                            border: `1px solid ${C.green}40`, background: 'none',
                            color: C.green, fontFamily: font.body, fontSize: 11, cursor: 'pointer',
                          }}>
                            ✓ анкета пройдена · повторить
                          </button>
                          <button onClick={async () => {
                            if (!confirm('Удалить все данные анкеты?')) return
                            await clearSurvey(userId)
                            setSurveyStatus({ group1_done: false, group2_done: false })
                            setSurveyStep(null)
                            setSurveyQuestions([])
                            setSurveyGroupNum(1)
                          }} style={{
                            padding: '5px 14px', borderRadius: 20,
                            border: `1px solid ${C.red}40`, background: 'none',
                            color: C.red, fontFamily: font.body, fontSize: 11, cursor: 'pointer',
                          }}>
                            ✕ анкета
                          </button>
                        </>
                      )}
                      <button onClick={() => setShowUpload(v => !v)} style={{
                        padding: '5px 14px', borderRadius: 20,
                        border: `1px solid ${C.border}`, background: 'none',
                        color: C.silverLo, fontFamily: font.body, fontSize: 11, cursor: 'pointer',
                      }}>
                        {showUpload ? '▲ Скрыть' : '↑ Обновить данные'}
                      </button>
                    </div>
                  )}

                  {!isGuest && showUpload && (
                    <div style={{ marginBottom: 24 }}>
                      <FileUpload onReady={runIngest} />
                    </div>
                  )}

                  <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: 24, overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none', touchAction: 'pan-x' }}>
                    {ANALYSIS_TABS.filter(t => !t.guestHidden || !isGuest).map(t => (
                      <button key={t.id} onClick={() => setAnalysisTab(t.id)} style={{
                        padding: '8px 14px', cursor: 'pointer', background: 'none', border: 'none',
                        fontFamily: font.body, fontSize: 12, flexShrink: 0, whiteSpace: 'nowrap',
                        color: analysisTab === t.id ? C.silverHi : C.silverLo,
                        borderBottom: `1.5px solid ${analysisTab === t.id ? C.silverHi : 'transparent'}`,
                        marginBottom: -1, transition: 'all 0.2s',
                      }}>
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {analysisTab === 'socionics'
                    ? <SocionicsTab userId={userId} isOwner={true} />
                    : analysisTab === 'physiognomy' && !isGuest
                    ? <PhysiognomyTab userId={userId} isOwner={true} />
                    : analysisTab === 'survey'
                    ? <>
                        <SurveyResultTab userId={userId} surveyResponses={surveyResponses} />
                        {surveyStatus?.group2_done && (
                          <GlassPanel style={{ padding: '20px 24px', marginTop: 24 }}>
                            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, fontWeight: 600, color: C.silverHi, marginBottom: 8 }}>
                              Дополнительные сессии
                            </div>
                            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: C.silverLo, margin: '0 0 16px', lineHeight: 1.6 }}>
                              Для большей точности ты можете ответить на дополнительные блоки вопросов. Каждая сессия генерируется на основе всех предыдущих ответов и исследует неопределённые области.
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', gap: 4 }}>
                                {[1,2,3,4,5].map(n => (
                                  <div key={n} style={{
                                    width: 28, height: 28, borderRadius: 4,
                                    border: `1px solid ${n <= surveySessionsCompleted ? C.accent : C.border}`,
                                    background: n <= surveySessionsCompleted ? `${C.accent}20` : 'none',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontFamily: 'Space Mono, monospace', fontSize: 11,
                                    color: n <= surveySessionsCompleted ? C.accent : C.muted,
                                  }}>{n <= surveySessionsCompleted ? '✓' : n}</div>
                                ))}
                              </div>
                              <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 11, color: C.silverLo }}>
                                {surveySessionsCompleted}/5 пройдено
                              </span>
                              {surveySessionsCompleted < 5 ? (
                                <button
                                  onClick={startExtraSession}
                                  disabled={surveySessionLoading}
                                  style={{ padding: '6px 16px', borderRadius: 6, border: `1px solid ${C.accent}60`, background: surveySessionLoading ? `${C.accent}15` : 'none', color: surveySessionLoading ? C.accent : C.silverHi, fontFamily: font.body, fontSize: 12, cursor: surveySessionLoading ? 'wait' : 'pointer' }}
                                >
                                  {surveySessionLoading ? '◎ Генерирую вопросы...' : `Пройти сессию ${surveySessionsCompleted + 1}`}
                                </button>
                              ) : (
                                <span style={{ fontFamily: font.body, fontSize: 12, color: C.accent }}>✓ Все сессии пройдены</span>
                              )}
                              {showSurveyAnalyzeBtn && (
                                <button
                                  onClick={async () => { setShowSurveyAnalyzeBtn(false); setSurveyStep('analyzing'); await runSurveyAnalysis() }}
                                  style={{ padding: '6px 16px', borderRadius: 6, border: `1px solid rgba(80,200,120,0.5)`, background: 'rgba(80,200,120,0.1)', color: 'rgba(140,220,160,0.9)', fontFamily: font.body, fontSize: 12, cursor: 'pointer' }}
                                >
                                  ↻ Обновить анализ
                                </button>
                              )}
                            </div>
                          </GlassPanel>
                        )}
                      </>
                    : <ProfileResult
                        profile={profile}
                        isOwner={false}
                        userId={userId}
                        activeTab={analysisTab}
                        onTabChange={setAnalysisTab}
                        isMobile={isMobile}
                      />
                  }
                </>
              )}
            </>
          )}

          {view === 'career' && <CareerNavigator userId={userId} isOwner={false} profileExists={!!profile} rolesState={careerRolesState} onRolesChange={setCareerRolesState} />}

          {view === 'natal' && <NatalChart userId={userId} isOwner={false} />}

          {view === 'music' && <MusicTab userId={userId} isOwner={!isGuest} ownerUserId="user" />}

          {view === 'development' && <Development userId={userId} isOwner={false} />}

          {view === 'mentor' && <Mentor userId={userId} isOwner={false} />}

          {view === 'facts' && (
            <div style={{ padding: isMobile ? '16px 12px 60px' : '24px 28px 60px' }}>
              <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? 22 : 28, fontWeight: 600, color: C.silverHi, margin: 0 }}>
                  Мои факты
                </h2>
                {extractionRunning && (
                  <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 11, color: C.accent, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ animation: 'spin 1.2s linear infinite', display: 'inline-block' }}>⟳</span>
                    извлекаю факты...
                  </span>
                )}
                {!isGuest && (
                  <button
                    onClick={async () => {
                      setFactsLoading(true)
                      try {
                        const jobId = await extractFactsStart(userId)
                        setExtractionJobId(jobId)
                      } catch (e) {
                        console.error('extract facts error', e)
                      } finally {
                        setFactsLoading(false)
                      }
                    }}
                    disabled={factsLoading || extractionRunning}
                    className={factsCount === null && dataStats && dataStats.total > 0 ? 'pulse-gold' : ''}
                    style={{ marginLeft: 'auto', padding: '4px 13px', borderRadius: 4, border: `1px solid ${C.accent}60`, background: 'none', color: C.silverLo, fontFamily: font.body, fontSize: 11, cursor: 'pointer', opacity: factsLoading ? 0.5 : 1 }}
                  >
                    {factsLoading ? '⏳ извлекаю...' : '↻ переизвлечь все'}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: `1px solid ${C.border}` }}>
                {[{ id: 'facts', label: 'Факты' }, { id: 'voice', label: 'Голос и манера' }].map(t => (
                  <button
                    key={t.id}
                    onClick={() => setFactsSubTab(t.id)}
                    style={{
                      padding: '8px 14px', background: 'none', border: 'none',
                      borderBottom: `2px solid ${factsSubTab === t.id ? C.accent : 'transparent'}`,
                      color: factsSubTab === t.id ? C.silverHi : C.silverLo,
                      fontFamily: font.body, fontSize: 12.5, cursor: 'pointer', marginBottom: -1,
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {factsSubTab === 'facts' ? (
                <FactsViewer
                  userId={userId}
                  onCountChange={setFactsCount}
                  extractionJobId={extractionJobId}                  isOwner={isOwnerVisiting}
                />
              ) : (
                <VoiceNotesViewer userId={userId} />
              )}
            </div>
          )}

          {view === 'twin' && (
            <div>
              <div style={{ fontFamily: font.heading, fontSize: 28, fontWeight: 300, color: C.silverHi, marginBottom: 8 }}>
                Мой цифровой двойник
              </div>
              <p style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>
                Пообщайся со своим AI-двойником — он отвечает на основе твоих данных.
              </p>

              {/* Кнопка настройки стиля двойника */}
              <div style={{ marginBottom: 24 }}>
                <button
                  onClick={configureTwin}
                  disabled={twinLoading || !profile}
                  style={{
                    padding: '8px 20px', borderRadius: 6, cursor: twinLoading || !profile ? 'default' : 'pointer',
                    fontFamily: font.body, fontSize: 13,
                    background: twinConfigured ? 'rgba(77,200,138,0.08)' : C.accentDim,
                    border: `1px solid ${twinConfigured ? 'rgba(77,200,138,0.3)' : C.borderHi}`,
                    color: twinConfigured ? C.green : C.accent,
                    opacity: twinLoading || !profile ? 0.5 : 1,
                    transition: 'all 0.2s',
                  }}
                >
                  {twinLoading ? '⏳ Настройка...' : twinConfigured ? '✓ Стиль настроен · обновить' : '✦ Настроить стиль двойника'}
                </button>
                {!profile && (
                  <span style={{ marginLeft: 12, fontFamily: font.body, fontSize: 12, color: C.textMuted }}>
                    Сначала загрузи данные в разделе «Мой профиль»
                  </span>
                )}
              </div>

              <VisitorChat profileUserId={userId} />

              {/* ── Публичная ссылка + видимость ── */}
              {canEditVisibility && (
              <div style={{ marginTop: 28, padding: '16px 20px', background: 'rgba(255,255,255,0.025)', borderRadius: 10, border: `1px solid ${C.border}` }}>
                <div style={{ fontFamily: font.heading, fontSize: 14, color: C.silverHi, marginBottom: 12 }}>
                  Публичный профиль
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                  <code style={{ flex: 1, fontFamily: font.mono, fontSize: 11, color: C.silverLo, background: 'rgba(255,255,255,0.04)', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {publicUrl}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(publicUrl).catch(() => {})
                      setLinkCopied(true)
                      setTimeout(() => setLinkCopied(false), 2000)
                    }}
                    style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.borderHi}`, background: 'transparent', color: linkCopied ? C.green : C.accent, fontFamily: font.mono, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {linkCopied ? '✓ скопировано' : '⎘ копировать'}
                  </button>
                </div>
                {publicSettings && (
                  <>
                    <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted, marginBottom: 8 }}>
                      что видно гостям:
                    </div>
                    <VisibilitySettings
                      settings={publicSettings}
                      saving={visibilitySaving}
                      onChange={async (next) => {
                        setPublicSettings(next)
                        setVisibilitySaving(true)
                        try {
                          await updateParticipantVisibility(participantId, next)
                        } catch (e) {
                          console.error(e)
                        } finally {
                          setVisibilitySaving(false)
                        }
                      }}
                    />
                  </>
                )}
              </div>
              )}

              {!isGuest && (
                <div style={{ marginTop: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <button
                      onClick={async () => {
                        if (showFacts && !extractionJobId) { setShowFacts(false); return }
                        setFactsLoading(true)
                        try {
                          const jobId = await extractFactsStart(userId)
                          setExtractionJobId(jobId)
                          setShowFacts(true)
                        } catch (e) {
                          console.error('extract facts error', e)
                        } finally {
                          setFactsLoading(false)
                        }
                      }}
                      disabled={factsLoading}
                      style={{
                        padding: '6px 14px', borderRadius: 6,
                        border: `1px solid ${C.border}`,
                        background: 'none',
                        color: factsLoading ? C.accent : C.textMuted,
                        fontFamily: font.mono, fontSize: 12, cursor: 'pointer',
                      }}
                    >
                      {factsLoading ? '⏳ извлекаю факты...' : factsCount !== null ? `◈ факты · ${factsCount}` : '◈ Извлечь факты о себе'}
                    </button>
                  </div>
                  {showFacts && (
                    <>
                      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, fontWeight: 300, color: C.silverHi, marginBottom: 12 }}>
                        ◈ Долгосрочная память двойника
                      </div>
                      <FactsViewer
                        userId={userId}
                        onCountChange={setFactsCount}
                        extractionJobId={extractionJobId}
                        isOwner={isOwnerVisiting}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
        )} {/* end view !== 'mindmap' */}
      </div>
    </div>
  )
}
